/**
 * @fileoverview Background component for rendering Backdrop (solid, photo, clip)
 * @module screen-view-v2/components/shared/Background
 *
 * V2 adaptation:
 * - Backdrop replaces BackgroundType
 * - backdrop.kind: 'solid' | 'photo' | 'clip' (was 'color' | 'image' | 'video')
 * - Type narrowing via backdrop.kind to access typed content
 * - Uses V2 Image/Video components with LocalizedImage/LocalizedClip
 */

import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type {
  Backdrop,
  SolidBackdrop,
  ImageBackdrop,
  ClipBackdrop,
} from '@segmently/schema-models/v2';
import { Image } from '../Image';
import { Video } from '../Video';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import type { PreviewMode } from '../../../types/previewMode';
import styles from './Background.module.css';

export interface BackgroundProps {
  backdrop: Backdrop | undefined;
  locale: string;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  previewMode?: PreviewMode;
}

export const Background = memo(function Background({
  backdrop,
  locale,
  className,
  'data-testid': testId,
  editPath,
  previewMode,
}: BackgroundProps): React.ReactElement | null {
  if (!backdrop) {
    return null;
  }

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  // V2: 'solid' replaces 'color'
  if (backdrop.kind === 'solid') {
    const solidContent = backdrop.content as SolidBackdrop;
    if (!solidContent.color) return null;

    const inlineStyle: CSSProperties = {
      backgroundColor: solidContent.color,
    };

    return (
      <div
        className={className ? `${styles.background} ${className}` : styles.background}
        style={inlineStyle}
        data-testid={schemaTestId}
        data-edit-path={editPath}
      />
    );
  }

  // V2: 'photo' replaces 'image'
  if (backdrop.kind === 'photo') {
    const imageContent = backdrop.content as ImageBackdrop;
    if (!imageContent.image) return null;

    return (
      <div
        className={className ? `${styles.background} ${className}` : styles.background}
        data-testid={schemaTestId}
        data-edit-path={editPath}
      >
        <Image
          image={imageContent.image}
          locale={locale}
          scaleMode="scaleAspectFill"
          className={className ? `${styles.background} ${styles.image} ${className}` : `${styles.background} ${styles.image}`}
          editPath={editPath}
          previewMode={previewMode}
        />
      </div>
    );
  }

  // V2: 'clip' replaces 'video'
  if (backdrop.kind === 'clip') {
    const clipContent = backdrop.content as ClipBackdrop;
    if (!clipContent.video) return null;

    return (
      <Video
        clip={clipContent.video}
        locale={locale}
        scaleMode="scaleAspectFill"
        className={className ? `${styles.background} ${styles.video} ${className}` : `${styles.background} ${styles.video}`}
        data-testid={schemaTestId}
        editPath={editPath}
        previewMode={previewMode}
      />
    );
  }

  return null;
});

Background.displayName = 'Background';
